import { useState, useEffect, useRef, useMemo } from "react";
import { toast } from "sonner";
import { useParams, useNavigate } from "react-router-dom";
import { saveAs } from "file-saver";
import { supabase } from '../../../shared/lib/supabase';
import { realtimeManager } from '../../../shared/lib/realtimeManager';
import { Molecule3DViewer } from '../../molecules/components/Molecule3DViewer';
import { convertJSONToPDB } from '../../molecules/utils/jsonToPdb';
import {
  Download,
  CheckCircle2,
  AlertTriangle,
  Clock,
  Activity,
  Zap,
  FileText,
  Dna,
  Rocket,
  ArrowRight,
  Settings,
  Search,
  Edit2,
  X,
  Check,
  FileCode,
  Split,
  Layers,
  ChevronLeft,
  ChevronRight
} from "lucide-react";
import { useTabManager } from '../../../shared/contexts/TabContext';
import { useTabNavigation } from '../../../shared/hooks/useTabNavigation';
import { RealtimeErrorBoundary } from '../../../shared/components/ui/RealtimeErrorBoundary';
import { EnhancedSequenceDisplay } from './EnhancedSequenceDisplay';

interface OdysseyJob {
  id: string;
  user_id?: string;
  project_id?: string;
  job_id: string;
  base_job_id?: string;
  name?: string;
  json_input?: string;  // Stores the entire JSON input
  config?: any;  // JSONB config
  status: 'pending' | 'processing' | 'completed' | 'failed';
  result?: any;  // JSONB result containing output URLs and metadata
  pdb_output?: string;  // Stored converted PDB content
  cif_output?: string;  // CIF output content
  pdb_content?: string; // Alternative PDB storage field
  comments?: string;
  current_stage?: string;
  stage_status?: string;
  stage_details?: string;
  stage_updated_at?: string;
  workflow_type?: 'enzyme' | 'antibody' | 'peptide';
  generation_id?: string;
  notes?: string;
  created_at: string;
  started_at?: string;
  completed_at?: string;
  updated_at: string;
}

interface ProteinMetadata {
  num_residues?: number;
  num_masked?: number;
  num_mutations?: number;
  generated_sequence?: string;
  original_sequence?: string;
  model_version?: string;
  processing_time_seconds?: number;
}

// Per-generation output rows from odyssey_outputs
interface OdysseyOutput {
  id: string;
  job_id: string; // odyssey_jobs.job_id
  output_index: number; // 0..n-1
  output_data: any; // JSONB from model
  pdb_output?: string | null; // per-gen PDB (raw text)
  cif_output?: string | null; // per-gen CIF (raw text)
  created_at?: string;
}

interface ResidueInfo {
  chain: string;
  resNum: number;
  resName: string;
  index: number;
}

interface SequenceData {
  chain: string;
  sequence: string;
  residues: ResidueInfo[];
}

// Status badge component
function StatusBadge({ status }: { status: string }) {
  const statusConfig = {
    pending: { bg: 'bg-gray-100', text: 'text-gray-800', icon: Clock },
    processing: { bg: 'bg-blue-100', text: 'text-blue-800', icon: Activity },
    completed: { bg: 'bg-green-100', text: 'text-green-800', icon: CheckCircle2 },
    failed: { bg: 'bg-red-100', text: 'text-red-800', icon: AlertTriangle }
  };

  const config = statusConfig[status as keyof typeof statusConfig] || statusConfig.pending;
  const Icon = config.icon;

  return (
    <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-sm font-medium ${config.bg} ${config.text}`}>
      <Icon className="h-4 w-4" />
      {status.charAt(0).toUpperCase() + status.slice(1)}
    </span>
  );
}

// Progress stages component
function StageProgress({ currentStage, status }: { currentStage?: string; status: string }) {
  const stages = [
    { id: 'queued', label: 'Queued' },
    { id: 'odyssey_processing', label: 'Protein Design' },
    { id: 'upload', label: 'Uploading' },
    { id: 'completed', label: 'Completed' }
  ];

  const getStageStatus = (stageId: string) => {
    if (status === 'completed') return 'completed';
    if (status === 'failed') return 'failed';
    
    const currentIndex = stages.findIndex(s => s.id === currentStage);
    const stageIndex = stages.findIndex(s => s.id === stageId);
    
    if (stageIndex < currentIndex) return 'completed';
    if (stageIndex === currentIndex) return 'active';
    return 'pending';
  };

  return (
    <div className="mb-10">
      {/* Top Row: Circles + Labels */}
      <div className="flex items-center justify-between mb-2">
        {stages.map((stage, index) => {
          const stageStatus = getStageStatus(stage.id);
          return (
            <div key={stage.id} className="relative">
              <div className={`
                w-10 h-10 rounded-full flex items-center justify-center text-sm font-medium
                ${stageStatus === 'completed' ? 'bg-green-500 text-white' : 
                  stageStatus === 'active' ? 'bg-blue-500 text-white animate-pulse' : 
                  stageStatus === 'failed' ? 'bg-red-500 text-white' :
                  'bg-gray-200 text-gray-500'}
              `}>
                {stageStatus === 'completed' ? '✓' : index + 1}
              </div>
              <span className="absolute -bottom-6 left-1/2 transform -translate-x-1/2 text-xs text-gray-600 whitespace-nowrap">
                {stage.label}
              </span>
            </div>
          );
        })}
      </div>
      
    </div>
  );
}

interface OdysseyJobDetailsProps {
  odysseyJobId?: string;
  lazyLoad?: boolean;
}

function OdysseyJobDetailsInner({ odysseyJobId: propJobId, lazyLoad = true }: OdysseyJobDetailsProps = {}) {
  const { jobId: paramJobId } = useParams<{ jobId: string }>();
  const jobId = propJobId || paramJobId;
  const navigate = useNavigate();
  const [job, setJob] = useState<OdysseyJob | null>(null);
  const [metadata, setMetadata] = useState<ProteinMetadata | null>(null);
  const [loading, setLoading] = useState(lazyLoad);
  const [hasLoadedData, setHasLoadedData] = useState(!lazyLoad);
  const [isConverting, setIsConverting] = useState(false);
  const [showNameModal, setShowNameModal] = useState(false);
  const [nextGenName, setNextGenName] = useState('');
  const [isOverlapMode, setIsOverlapMode] = useState(false);
  const [moleculeViewerId, setMoleculeViewerId] = useState<string | null>(null);
  const [moleculeViewerName, setMoleculeViewerName] = useState<string | null>(null);
  const [sequences, setSequences] = useState<SequenceData[]>([]);
  const [highlightedResidues, setHighlightedResidues] = useState<string[]>([]);
  const [originalPdbContent, setOriginalPdbContent] = useState<string | null>(null);
  const [originalSequences, setOriginalSequences] = useState<SequenceData[]>([]);
  // Multi-generation state
  const [outputs, setOutputs] = useState<OdysseyOutput[]>([]);
  const [selectedGen, setSelectedGen] = useState<number>(0);
  const [genPage, setGenPage] = useState<number>(0); // 0-based pages of 12

  // Currently selected output row
  const selectedOutput = useMemo(() => {
    return outputs.length > 0 ? outputs.find(o => o.output_index === selectedGen) ?? outputs[0] : null;
  }, [outputs, selectedGen]);

  // Ensure page reflects the selected generation (so the selected number is visible)
  useEffect(() => {
    setGenPage(Math.floor(selectedGen / 12));
  }, [selectedGen]);
  const subscriptionRef = useRef<(() => void) | null>(null);
  const { openTab } = useTabManager();
  const { openMoleculeTab } = useTabNavigation();
  
  // Lazy loading handler
  const handleLoadData = () => {
    if (!hasLoadedData) {
      setHasLoadedData(true);
      setLoading(true);
    }
  };
  
  // Notes state
  const [notes, setNotes] = useState('');
  const [isEditingNotes, setIsEditingNotes] = useState(false);
  const [originalNotes, setOriginalNotes] = useState('');
  const [savingNotes, setSavingNotes] = useState(false);

  // Memoize structure colors and labels to prevent unnecessary component remounting
  const overlayStructureColors = useMemo(() => {
    return originalPdbContent ? ['#808080', '#3B82F6'] : ['#3B82F6'];
  }, [originalPdbContent]);

  const overlayStructureLabels = useMemo(() => {
    return originalPdbContent ? ['Original', 'Generated'] : ['Generated'];
  }, [originalPdbContent]);

  const overlayPdbContents = useMemo(() => {
    const currentPdb = selectedOutput?.pdb_output || job?.pdb_output || job?.pdb_content;
    return [originalPdbContent, currentPdb].filter((content): content is string => Boolean(content));
  }, [originalPdbContent, selectedOutput?.pdb_output, job?.pdb_output, job?.pdb_content]);

  useEffect(() => {
    if (!jobId) {
      // Only show error and navigate if we're in standalone mode (not tab mode)
      if (!propJobId) {
        toast.error("No job ID provided");
        navigate('/');
      }
      return;
    }

    // Only load data if not using lazy loading or if user has clicked to load
    if (!lazyLoad || hasLoadedData) {
      loadJob();
      setupRealtimeSubscription();
    }

    let pollInterval: NodeJS.Timeout | null = null;
    
    // Only set up polling if data is loaded and job is incomplete
    if (hasLoadedData && job && (job.status === 'processing' || job.status === 'pending')) {
      pollInterval = setInterval(() => {
        loadJob();
      }, job.status === 'processing' ? 3000 : 5000); // More frequent for processing jobs
    }

    return () => {
      if (pollInterval) {
        clearInterval(pollInterval);
      }
      if (subscriptionRef.current) {
        subscriptionRef.current();
        subscriptionRef.current = null;
      }
    };
  }, [jobId, job?.status, lazyLoad, hasLoadedData]);

  // Fetch per-generation outputs once the job is loaded or status changes to completed
  useEffect(() => {
    const loadOutputs = async () => {
      if (!jobId) return;
      try {
        const { data, error } = await supabase
          .from('odyssey_outputs')
          .select('id, job_id, output_index, output_data, pdb_output, cif_output, created_at')
          .eq('job_id', jobId)
          .order('output_index', { ascending: true });
        if (error) throw error;
        if (data && data.length > 0) {
          setOutputs(data as unknown as OdysseyOutput[]);
          // Default to first generation
          setSelectedGen((prev) => (prev < data.length ? prev : 0));
        } else {
          setOutputs([]);
        }
      } catch (e) {
        console.error('Failed to load odyssey_outputs:', e);
      }
    };

    // Load when job becomes complete or when jobId changes
    if (jobId && job?.status === 'completed') {
      loadOutputs();
    }
  }, [jobId, job?.status]);

  // When selected output changes, update generated sequences from its PDB
  useEffect(() => {
    const currentPdb = selectedOutput?.pdb_output || job?.pdb_output || job?.pdb_content;
    if (currentPdb) {
      try {
        const parsedSequences = parseSequenceFromPDB(currentPdb);
        setSequences(parsedSequences);
      } catch (e) {
        console.error('Failed to parse PDB for selected generation:', e);
      }
    }
  }, [selectedOutput?.pdb_output, job?.pdb_output, job?.pdb_content]);

  // Load molecule viewer ID for input configuration
  useEffect(() => {
    const loadMoleculeViewerId = async () => {
      if (!jobId) return;
      
      try {
        const { data, error } = await supabase
          .from('molecule_viewer')
          .select('id, name')
          .eq('odyssey_job_id', jobId)
          .single();
          
        if (error) {
          setMoleculeViewerId(null);
          setMoleculeViewerName(null);
        } else {
          setMoleculeViewerId(data.id);
          setMoleculeViewerName(data.name);
          
          // If job is already loaded and complete, fetch mask data and original structure
          if (job?.status === 'completed' && (job.pdb_output || job.pdb_content)) {
            const fetchAndSetData = async () => {
              // Fetch mask data
              const maskData = await fetchMaskData(data.id);
              setHighlightedResidues(maskData);
              
              // Fetch original structure using job ID
              const originalPdb = await fetchOriginalStructure(job.job_id);
              if (originalPdb) {
                setOriginalPdbContent(originalPdb);
                // Parse original sequences
                const originalSeqs = parseSequenceFromPDB(originalPdb);
                setOriginalSequences(originalSeqs);
              }
            };
            fetchAndSetData();
          }
        }
      } catch (error) {
        console.error('Error loading molecule viewer data:', error);
        setMoleculeViewerId(null);
        setMoleculeViewerName(null);
      }
    };

    loadMoleculeViewerId();
  }, [jobId, job?.status, job?.pdb_output, job?.pdb_content]);

  const parseMetadata = (result: any) => {
    if (!result) {
      return null;
    }

    // NEW FORMAT: Check result.metadata first (where push worker stores the metadata)
    if (result.metadata) {
      try {
        return {
          num_residues: result.metadata.num_residues || 0,
          num_masked: result.metadata.num_masked || 0,
          num_mutations: result.metadata.num_mutations || 0,
          generated_sequence: result.metadata.generated_sequence || '',
          original_sequence: result.metadata.original_sequence || '',
          processing_time_seconds: result.metadata.processing_time_seconds || 0,
          model_version: result.metadata.model_version || 'Odyssey'
        };
      } catch (e) {
        console.error('Error parsing result.metadata:', e);
      }
    }

    // FALLBACK: Try to extract from json_output
    if (result.json_output) {
      try {
        const jsonOutput = typeof result.json_output === 'string'
          ? JSON.parse(result.json_output)
          : result.json_output;
        
        return {
          num_residues: jsonOutput.sequence?.length || 0,
          num_masked: (jsonOutput.sequence?.match(/_/g) || []).length,
          num_mutations: 0, // Calculate if needed
          generated_sequence: jsonOutput.sequence || '',
          original_sequence: '',
          processing_time_seconds: 0,
          model_version: 'Odyssey'
        };
      } catch (e) {
        console.error('Error parsing json_output:', e);
      }
    }

    // LEGACY FORMAT: output_metadata
    if (result.output_metadata) {
      try {
        return result.output_metadata;
      } catch (e) {
        console.error('Error parsing output_metadata:', e);
      }
    }

    // ALTERNATIVE: Check if metadata is at root level
    if (result.num_residues !== undefined || result.generated_sequence) {
      return {
        num_residues: result.num_residues || 0,
        num_masked: result.num_masked || 0,
        num_mutations: result.num_mutations || 0,
        generated_sequence: result.generated_sequence || '',
        original_sequence: result.original_sequence || '',
        processing_time_seconds: result.processing_time_seconds || 0,
        model_version: result.model_version || 'Odyssey'
      };
    }

    return null;
  };

  // Parse PDB content to extract sequence data
  const parseSequenceFromPDB = (pdbContent: string): SequenceData[] => {
    const lines = pdbContent.split('\n');
    const sequenceMap = new Map<string, ResidueInfo[]>();
    
    // Parse ATOM records to extract residues
    lines.forEach(line => {
      if (line.startsWith('ATOM')) {
        const chainId = line.substring(21, 22).trim() || 'A';
        const resName = line.substring(17, 20).trim();
        const resNum = parseInt(line.substring(22, 26).trim());
        
        if (!sequenceMap.has(chainId)) {
          sequenceMap.set(chainId, []);
        }
        
        const existing = sequenceMap.get(chainId)!.find(r => r.resNum === resNum);
        if (!existing) {
          sequenceMap.get(chainId)!.push({
            chain: chainId,
            resNum,
            resName,
            index: sequenceMap.get(chainId)!.length
          });
        }
      }
    });

    // Convert to SequenceData array
    const sequences: SequenceData[] = [];
    sequenceMap.forEach((residues, chain) => {
      // Sort by residue number
      residues.sort((a, b) => a.resNum - b.resNum);
      // Update indices after sorting
      residues.forEach((residue, idx) => {
        residue.index = idx;
      });
      
      sequences.push({
        chain,
        sequence: residues.map(r => r.resName).join(''),
        residues
      });
    });

    return sequences;
  };

  // Fetch mask data from molecule_viewer table
  const fetchMaskData = async (moleculeId: string): Promise<string[]> => {
    try {
      const { data, error } = await supabase
        .from('molecule_viewer')
        .select('sequence_masks, structure_masks, sasa_masks, plddt_masks')
        .eq('id', moleculeId)
        .single();
        
      if (error || !data) {
        console.warn('No mask data found for molecule:', error);
        return [];
      }

      // Combine all mask types into one array
      const allMaskedResidues = new Set<string>();
      
      // Parse each mask type
      [data.sequence_masks, data.structure_masks, data.sasa_masks, data.plddt_masks].forEach(maskData => {
        if (maskData) {
          try {
            const parsedMasks = JSON.parse(maskData);
            if (Array.isArray(parsedMasks)) {
              parsedMasks.forEach(mask => allMaskedResidues.add(mask));
            }
          } catch (e) {
            console.warn('Error parsing mask data:', e);
          }
        }
      });

      return Array.from(allMaskedResidues);
    } catch (error) {
      console.error('Error fetching mask data:', error);
      return [];
    }
  };

  // Fetch original structure from molecule_viewer table by odyssey_job_id (mirrors ProteinDesignInput approach)
  const fetchOriginalStructure = async (odysseyJobId: string): Promise<string | null> => {
    try {
      console.log('Fetching original structure for Odyssey job:', odysseyJobId);
      
      // Query for the molecule_viewer record using the same approach as ProteinDesignInput
      const { data: molecule, error: queryError } = await supabase
        .from('molecule_viewer')
        .select('storage_path, name, ran_odyssey')
        .eq('odyssey_job_id', odysseyJobId)
        .single();
        
      if (queryError || !molecule) {
        console.warn('No molecule_viewer record found for Odyssey job:', odysseyJobId, queryError);
        return null;
      }

      console.log('Found molecule_viewer record:', molecule);

      if (!molecule.storage_path) {
        console.warn('No storage_path found in molecule_viewer record');
        return null;
      }

      // Use Supabase storage download API (same as ProteinDesignInput)
      console.log('Downloading PDB from storage path:', molecule.storage_path);
      const { data: fileData, error: downloadError } = await supabase.storage
        .from('molecule-files')
        .download(molecule.storage_path);

      if (downloadError) {
        console.error('Error downloading original structure:', downloadError);
        return null;
      }

      // Convert blob to text
      const pdbContent = await fileData.text();
      console.log('Successfully fetched original PDB content via storage API');
      return pdbContent;

    } catch (error) {
      console.error('Error in fetchOriginalStructure:', error);
      return null;
    }
  };


  const convertAndStorePDB = async (jobData: OdysseyJob) => {
    // Only convert if job is completed and has JSON output but no stored PDB yet
    if (jobData.status !== 'completed' || jobData.pdb_output || isConverting) {
      return;
    }

    const result = jobData.result ? 
      (typeof jobData.result === 'string' ? JSON.parse(jobData.result) : jobData.result) 
      : null;
    
    // Check if we have JSON data to convert
    const jsonData = result?.json_output;
    
    if (!jsonData) {
      return;
    }

    setIsConverting(true);

    try {
      // Parse JSON if it's a string
      const jsonToConvert = typeof jsonData === 'string' ? JSON.parse(jsonData) : jsonData;
      
      // Convert JSON to PDB format
      const pdbContent = convertJSONToPDB(jsonToConvert);
      
      // Update the odyssey_jobs table with the converted PDB
      const { data, error } = await supabase
        .from('odyssey_jobs')
        .update({ pdb_output: pdbContent })
        .eq('job_id', jobData.job_id)
        .select()
        .single();

      if (error) {
        throw error;
      }
      
      // Update local state with the new data
      if (data) {
        setJob(data);
      }
      
      toast.success('PDB file generated and saved');
    } catch (error: any) {
      console.error('Error converting/storing PDB:', error);
      // Don't show error toast - fail silently and let user use manual conversion
    } finally {
      setIsConverting(false);
    }
  };

  const loadJob = async () => {
    try {
      // Only set loading to true if we don't have a job yet (initial load)
      if (!job) {
        setLoading(true);
      }
      
      const { data, error } = await supabase
        .from('odyssey_jobs')
        .select('*')
        .eq('job_id', jobId!)
        .single();

      if (error) throw error;
      
      if (!data) {
        toast.error("Job not found");
        navigate('/');
        return;
      }

      setJob(data);

      // Always initialize notes from database (clear if no notes)
      setNotes(data.notes || '');
      setOriginalNotes(data.notes || '');

      // Parse metadata from result field if available
      if (data.result) {
        try {
          const result = typeof data.result === 'string' 
            ? JSON.parse(data.result)
            : data.result;
          
          const parsedMetadata = parseMetadata(result);
          setMetadata(parsedMetadata);
        } catch (e) {
          console.error('Failed to parse result:', e);
        }
      }

      // Auto-convert JSON to PDB if job is completed and has no PDB yet
      if (data.status === 'completed' && !data.pdb_output) {
        await convertAndStorePDB(data);
      }

      // Parse sequences and fetch mask data if job is complete and has PDB content
      if (data.status === 'completed' && (data.pdb_output || data.pdb_content)) {
        const pdbContent = data.pdb_output || data.pdb_content;
        if (pdbContent) {
          const parsedSequences = parseSequenceFromPDB(pdbContent);
          setSequences(parsedSequences);

          // Fetch mask data and original structure if we have a molecule viewer ID
          if (moleculeViewerId) {
            const maskData = await fetchMaskData(moleculeViewerId);
            setHighlightedResidues(maskData);
            
            // Use job ID to fetch original structure
            const originalPdb = await fetchOriginalStructure(data.job_id);
            if (originalPdb) {
              setOriginalPdbContent(originalPdb);
              const originalSeqs = parseSequenceFromPDB(originalPdb);
              setOriginalSequences(originalSeqs);
            }
          }
        }
      }
    } catch (error) {
      console.error('Error loading job:', error);
      toast.error('Failed to load job details');
    } finally {
      // Only set loading to false if we were loading
      if (loading) {
        setLoading(false);
      }
    }
  };

  const setupRealtimeSubscription = () => {
    // Clean up existing subscription first
    if (subscriptionRef.current) {
      subscriptionRef.current();
      subscriptionRef.current = null;
    }

    if (!jobId) return;
    
    // Create unique channel name to prevent conflicts
    const channelName = `odyssey-job-${jobId}`;
    
    try {
      // Use centralized realtime manager
      const unsubscribe = realtimeManager.subscribe(
        channelName,
        async (payload: any) => {
          try {
            // Defensive check - ensure payload has expected structure
            if (!payload?.new) {
              console.warn('Invalid realtime payload received:', payload);
              return;
            }

            setJob(payload.new);
            
            // Update metadata from result field if changed
            if (payload.new.result) {
              try {
                const result = typeof payload.new.result === 'string'
                  ? JSON.parse(payload.new.result)
                  : payload.new.result;
                
                const parsedMetadata = parseMetadata(result);
                setMetadata(parsedMetadata);
              } catch (e) {
                console.error('Failed to parse result from realtime update:', e);
              }
            }

            // Auto-convert JSON to PDB if job just completed and has no PDB yet
            if (payload.new.status === 'completed' && 
                !payload.new.pdb_output && 
                payload.old?.status !== 'completed') {
              await convertAndStorePDB(payload.new);
            }
          } catch (error) {
            console.error('Error processing realtime update:', error);
          }
        },
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'odyssey_jobs',
          filter: `job_id=eq.${jobId}`
        }
      );
      
      subscriptionRef.current = unsubscribe;
    } catch (error) {
      console.error('Failed to create realtime subscription:', error);
      subscriptionRef.current = null;
    }
  };

  const handleDownload = async (urlOrContent: string, filename: string, isRawContent: boolean = false) => {
    try {
      toast.info(`Downloading ${filename}...`);
      
      let blob: Blob;
      if (isRawContent) {
        // Create blob from raw content
        const mimeType = filename.endsWith('.pdb') ? 'chemical/x-pdb' : 'application/json';
        blob = new Blob([urlOrContent], { type: mimeType });
      } else {
        // Fetch from URL
        const response = await fetch(urlOrContent);
        blob = await response.blob();
      }
      
      saveAs(blob, filename);
      toast.success(`Downloaded ${filename}`);
    } catch (error) {
      console.error('Download error:', error);
      toast.error(`Failed to download ${filename}`);
    }
  };

  const createBlobUrl = (content: string, mimeType: string): string => {
    const blob = new Blob([content], { type: mimeType });
    return URL.createObjectURL(blob);
  };

  const parsePDBContent = (content: string): { atomCount: number; residueCount: number; chainCount: number } => {
    try {
      const lines = content.split('\n');
      
      const atoms = new Set<string>();
      const residues = new Set<string>();
      const chains = new Set<string>();
      
      lines.forEach(line => {
        if (line.startsWith('ATOM') || line.startsWith('HETATM')) {
          const atomSerial = line.substring(6, 11).trim();
          atoms.add(atomSerial);
          
          const resSeq = line.substring(22, 26).trim();
          residues.add(resSeq);
          
          const chainId = line.substring(21, 22).trim();
          if (chainId) chains.add(chainId);
        }
      });
      
      return {
        atomCount: atoms.size,
        residueCount: residues.size,
        chainCount: chains.size || 1
      };
    } catch (error) {
      console.error('Error parsing PDB content:', error);
      return { atomCount: 0, residueCount: 0, chainCount: 0 };
    }
  };


  const handleRunNextGeneration = async () => {
    const hasSelected = !!selectedOutput?.pdb_output;
    const hasJobLevel = !!(job && (job.pdb_output || job.pdb_content));
    if (!hasSelected && !hasJobLevel) {
      toast.error('No PDB output available for next generation');
      return;
    }
    // Show the naming modal
    setNextGenName('');
    setShowNameModal(true);
  };

  const handleConfirmNextGeneration = async () => {
    if (!nextGenName.trim()) return;
    
    // Check if there is data to use for next generation
    if (!job || (!selectedOutput?.pdb_output && !job.pdb_output && !job.pdb_content)) {
      toast.error('Job data is not available');
      return;
    }
    
    setShowNameModal(false);
    const toastId = toast.loading('Preparing next generation...');

    try {
      // Get authenticated user
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        throw new Error('Please sign in to continue');
      }

      // First, find the parent molecule_viewer entry that was used for this Odyssey job
      let parentMoleculeId: string | null = null;
      
      // Query for the molecule_viewer entry with this odyssey_job_id and ran_odyssey = true
      const { data: parentMolecules } = await supabase
        .from('molecule_viewer')
        .select('id')
        .eq('odyssey_job_id', job.job_id)
        .eq('ran_odyssey', true)
        .order('created_at', { ascending: true })
        .limit(1);
      
      if (parentMolecules && parentMolecules.length > 0) {
        parentMoleculeId = parentMolecules[0].id;
        console.log('Found parent molecule:', parentMoleculeId);
      }

      // Use currently selected generation's PDB when available; fall back to job-level
      const pdbContent = selectedOutput?.pdb_output || job.pdb_output || job.pdb_content;
      if (!pdbContent) {
        toast.error('No PDB content available');
        return;
      }
      const pdbMetadata = parsePDBContent(pdbContent);

      // Generate file name
      const currentIdx = selectedOutput?.output_index;
      const oneBased = currentIdx !== undefined ? currentIdx + 1 : undefined;
      const fileName = oneBased
        ? `odyssey-${job.job_id}-gen-${oneBased}.pdb`
        : `odyssey-${job.job_id}-output.pdb`;
      const storagePath = `${user.id}/${job.project_id}/${Date.now()}-${fileName}`;

      // Calculate expiry for staging molecules (7 days). If the user never runs Odyssey,
      // cleanup-expired-molecules will remove them after expiry.
      const expiryDate = new Date();
      expiryDate.setDate(expiryDate.getDate() + 7);

      // Create molecule_viewer record FIRST (required for storage RLS policy)
      // We'll update the file_url after successful upload
      const { data: molecule, error: dbError } = await supabase
        .from('molecule_viewer')
        .insert({
          user_id: user.id,
          project_id: job.project_id,
          file_name: fileName,
          file_size: pdbContent.length,
          file_url: '', // Will be updated after upload
          file_type: 'pdb',
          storage_path: storagePath,
          atom_count: pdbMetadata.atomCount,
          residue_count: pdbMetadata.residueCount,
          chain_count: pdbMetadata.chainCount,
          previous_generation: parentMoleculeId,  // Link to parent instead of copying odyssey_job_id
          // odyssey_job_id removed - will be set when this generation runs Odyssey
          name: nextGenName,
          metadata: {
            previous_generation_info: {
              job_id: job.job_id,
              output_index: selectedOutput?.output_index ?? null,
              total_outputs: outputs && outputs.length ? outputs.length : 1,
            }
          },
          expires_at: expiryDate.toISOString()
        })
        .select()
        .single();

      if (dbError) throw dbError;

      // Now upload PDB to storage (RLS policy can find the molecule_viewer record)
      const { error: uploadError } = await supabase.storage
        .from('molecule-files')
        .upload(storagePath, pdbContent, {
          contentType: 'chemical/x-pdb',
          upsert: false
        });

      if (uploadError) {
        // If upload fails, clean up the molecule_viewer record
        await supabase
          .from('molecule_viewer')
          .delete()
          .eq('id', molecule.id);
        throw uploadError;
      }

      // Get public URL and update the molecule_viewer record
      const { data: { publicUrl } } = supabase.storage
        .from('molecule-files')
        .getPublicUrl(storagePath);

      const { error: updateError } = await supabase
        .from('molecule_viewer')
        .update({ file_url: publicUrl })
        .eq('id', molecule.id);

      if (updateError) throw updateError;

      toast.success('Opening Protein Design for next generation', { id: toastId });

      console.log('🎯 About to call openMoleculeTab with:', { moleculeId: molecule.id, nextGenName });
      
      // Open the molecule tab using proper navigation
      openMoleculeTab(molecule.id, nextGenName, nextGenName);

    } catch (error: any) {
      console.error('Error preparing next generation:', error);
      
      // Provide specific error messages based on the type of error
      let errorMessage = 'Failed to prepare next generation';
      
      if (error?.message?.includes('duplicate key')) {
        errorMessage = 'A generation with this name already exists. Please choose a different name.';
      } else if (error?.message?.includes('storage') || error?.message?.includes('upload')) {
        errorMessage = 'Failed to upload file. Please check your permissions and try again.';
      } else if (error?.message?.includes('auth') || error?.message?.includes('permission')) {
        errorMessage = 'You do not have permission to create generations in this project.';
      } else if (error?.message) {
        errorMessage = error.message;
      }
      
      toast.error(errorMessage, { id: toastId });
    }
  };

  const saveNotes = async () => {
    if (!jobId) return;
    
    try {
      setSavingNotes(true);
      
      const { error } = await supabase
        .from('odyssey_jobs')
        .update({ notes: notes.trim() })
        .eq('job_id', jobId);
      
      if (error) throw error;
      
      // Reload job data from database to ensure fresh, accurate state
      await loadJob();
      
      setOriginalNotes(notes.trim());
      setIsEditingNotes(false);
      toast.success('Notes saved');
    } catch (error) {
      console.error('Error saving notes:', error);
      toast.error('Failed to save notes');
    } finally {
      setSavingNotes(false);
    }
  };

  const cancelNotesEdit = () => {
    setNotes(originalNotes);
    setIsEditingNotes(false);
  };

  // Determine if we're in tab mode or standalone mode
  const isTabMode = !!propJobId;
  const containerClass = isTabMode ? "min-h-full bg-white" : "min-h-screen bg-white py-8";
  const loadingContainerClass = isTabMode ? "flex items-center justify-center min-h-full bg-white" : "flex items-center justify-center min-h-screen bg-white";
  
  if (loading) {
    return (
      <div className={loadingContainerClass}>
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading Odyssey job...</p>
        </div>
      </div>
    );
  }

  if (!job) {
    return (
      <div className={loadingContainerClass}>
        <div className="text-center">
          <AlertTriangle className="h-12 w-12 text-red-500 mx-auto mb-4" />
          <p className="text-gray-600">Job not found</p>
        </div>
      </div>
    );
  }

  const isComplete = job.status === 'completed';
  const isFailed = job.status === 'failed';
  const isProcessing = job.status === 'processing';

  // If lazy loading is enabled and data hasn't been loaded yet, show click to load UI
  if (lazyLoad && !hasLoadedData) {
    return (
      <div className={containerClass}>
        <div className={isTabMode ? "h-full p-6" : "max-w-7xl mx-auto px-4 sm:px-6 lg:px-8"}>
          <div className="flex items-center justify-center h-full bg-gray-50 rounded-lg shadow-sm">
            <div className="text-center p-8">
              <Dna className="h-16 w-16 text-gray-300 mx-auto mb-4" />
              <h2 className="text-xl font-medium text-gray-900 mb-2">Odyssey Generation Details</h2>
              <p className="text-gray-500 mb-6">Click to load generation details and results</p>
              <button
                onClick={handleLoadData}
                className="bg-gray-900 hover:bg-gray-800 text-white px-6 py-3 rounded-md transition-colors font-medium"
              >
                Load Generation
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Parse result field and determine format
  const result = job.result ? (typeof job.result === 'string' ? JSON.parse(job.result) : job.result) : null;
  
  // Check if we have raw content format (json_output, pdb_output) or URL format (output_pdb_url)
  const hasRawContent = result?.pdb_output !== undefined;
  
  // Priority: Use stored PDB from database first, then raw content, then URL
  const storedPdb = selectedOutput?.pdb_output || job.pdb_output || job.pdb_content;
  const pdbUrl = storedPdb
    ? createBlobUrl(storedPdb, 'chemical/x-pdb')
    : (hasRawContent && result.pdb_output
      ? createBlobUrl(result.pdb_output, 'chemical/x-pdb')
      : result?.output_pdb_url);

  return (
    <>
      <div className={containerClass}>
      <div className={isTabMode ? "h-full p-6" : "max-w-7xl mx-auto px-4 sm:px-6 lg:px-8"}>
        {/* Header */}
        <div className="bg-gray-50 rounded-lg shadow-sm p-6 mb-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
                <Dna className="h-6 w-6 text-purple-600" />
                {job.name || 'Odyssey Protein Design'}
              </h1>
              <p className="text-sm text-gray-500 mt-1">Job ID: {job.job_id}</p>
            </div>
            <StatusBadge status={job.status} />
          </div>

          {/* Stage Progress */}
          {!isFailed && (
            <div className="mt-8 mb-4">
              <StageProgress currentStage={job.current_stage} status={job.status} />
            </div>
          )}

          {/* Notes Section */}
          <div className="mt-4 pt-4 border-t border-gray-200">
            {isEditingNotes ? (
              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-700">
                  Notes
                </label>
                <textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-gray-500 focus:border-gray-500 text-sm"
                  placeholder="Add notes about this Odyssey generation..."
                />
                <div className="flex items-center gap-2">
                  <button
                    onClick={saveNotes}
                    className="flex items-center gap-1 px-3 py-1.5 text-sm bg-gray-900 text-white rounded-md hover:bg-gray-800 transition-colors"
                  >
                    <Check className="w-3 h-3" />
                    Save
                  </button>
                  <button
                    onClick={cancelNotesEdit}
                    className="flex items-center gap-1 px-3 py-1.5 text-sm text-gray-600 hover:text-gray-900 hover:bg-gray-50 rounded-md transition-colors"
                  >
                    <X className="w-3 h-3" />
                    Cancel
                  </button>
                </div>
              </div>
            ) : (
              <div className="flex items-start justify-between">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Notes
                  </label>
                  <p className="text-sm text-gray-900">
                    {notes || <span className="text-gray-500 italic">No notes</span>}
                  </p>
                </div>
                <button
                  onClick={() => setIsEditingNotes(true)}
                  className="flex items-center gap-1 px-2 py-1 text-xs text-gray-600 hover:text-gray-900 hover:bg-gray-50 border border-gray-300 rounded transition-colors"
                >
                  <Edit2 className="w-4 h-4" />
                  Edit
                </button>
              </div>
            )}
          </div>

        </div>

        {/* Main Content */}
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
          {/* Protein Structure Display - Left Side */}
          <div className="lg:col-span-3">
            <div className="bg-gray-50 rounded-lg shadow-sm p-6 h-full">
              <div className="flex items-center justify-between mb-2">
                <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                  <Zap className="h-5 w-5" />
                  Protein Design Results
                </h2>
              </div>
              {/* Toolbar moved inside white content box for perfect alignment */}

              {isComplete && (pdbUrl || storedPdb) && !savingNotes ? (
                <div className="bg-white rounded-lg p-8 pb-6 relative flex-1 flex flex-col mt-4">
                  {/* Aligned Toolbar (inside white box) */}
                  <div className="mb-6 flex items-center justify-between">
                    {/* Generation selector */}
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-gray-600 shrink-0">Generation:</span>
                      <div className="flex items-center gap-1">
                        {/* Page window of up to 12 (with optional arrows) */}
                        {(() => {
                          const total = outputs.length > 0 ? outputs.length : 1;
                          const start = genPage * 12;
                          const end = Math.min(total, start + 12);
                          const indices = Array.from({ length: end - start }, (_, i) => start + i);
                          const maxPage = Math.max(0, Math.ceil(total / 12) - 1);
                          const hasPrev = genPage > 0;
                          const hasNext = genPage < maxPage;
                          return (
                            <>
                              {hasPrev && (
                                <button
                                  onClick={() => setGenPage((p) => Math.max(0, p - 1))}
                                  className="px-2 py-1 rounded-md text-xs font-medium transition-colors border border-gray-200 text-gray-700 hover:bg-gray-50"
                                  title="Previous 12"
                                >
                                  <ChevronLeft className="h-4 w-4" />
                                </button>
                              )}
                              {indices.map((idx) => (
                                <button
                                  key={idx}
                                  onClick={() => setSelectedGen(idx)}
                                  className={`min-w-8 px-2 py-1 rounded-md text-xs font-medium transition-colors ${
                                    selectedGen === idx ? 'bg-gray-700 text-white' : 'text-gray-700 hover:bg-gray-50'
                                  }`}
                                  title={`Show generation ${idx + 1}`}
                                >
                                  {idx + 1}
                                </button>
                              ))}
                              {hasNext && (
                                <button
                                  onClick={() => setGenPage((p) => Math.min(maxPage, p + 1))}
                                  className="px-2 py-1 rounded-md text-xs font-medium transition-colors border border-gray-200 text-gray-700 hover:bg-gray-50"
                                  title="Next 12"
                                >
                                  <ChevronRight className="h-4 w-4" />
                                </button>
                              )}
                            </>
                          );
                        })()}
                      </div>
                    </div>
                    {/* View mode toggle */}
                    <div className="flex items-center gap-2 border border-gray-200 rounded-lg p-1 bg-transparent">
                      <button
                        onClick={() => setIsOverlapMode(false)}
                        className={`flex items-center gap-2 px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                          !isOverlapMode
                            ? 'bg-gray-500 text-white'
                            : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                        }`}
                      >
                        <Split className="h-4 w-4" />
                        Side-by-Side
                      </button>
                      <button
                        onClick={() => setIsOverlapMode(true)}
                        className={`flex items-center gap-2 px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                          isOverlapMode
                            ? 'bg-gray-500 text-white'
                            : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                        }`}
                      >
                        <Layers className="h-4 w-4" />
                        Overlay
                      </button>
                    </div>
                  </div>

                  <div className="space-y-8 flex-1 flex flex-col">
                    {/* Comparison View */}
                    {!isOverlapMode ? (
                      /* Side-by-Side Mode */
                      <div className="flex flex-1 min-h-80">
                        {/* Original Side - Left */}
                        <div className="flex-1 pr-4 flex flex-col">
                          <div className="flex items-center justify-between mb-6">
                            <h3 className="text-lg font-medium text-gray-700">Original Structure</h3>
                            {/* Legend for red highlights */}
                            <div className="flex items-center gap-2 text-sm text-gray-600">
                              <span className="inline-block w-4 h-0.5 bg-red-400"></span>
                              <span>= masked positions</span>
                            </div>
                          </div>

                          {/* Original Sequence */}
                          {originalSequences.length > 0 ? (
                            <div className="mb-6">
                              <h4 className="text-sm font-medium text-gray-600 mb-3">Sequence View</h4>
                              <EnhancedSequenceDisplay
                                sequences={originalSequences}
                                highlightedResidues={highlightedResidues}
                                highlightColor="red"
                                maxRows={6}
                              />
                            </div>
                          ) : metadata?.original_sequence && sequences.length > 0 ? (
                            <div className="mb-6">
                              <h4 className="text-sm font-medium text-gray-600 mb-3">Sequence View</h4>
                              <EnhancedSequenceDisplay
                                sequences={sequences}
                                highlightedResidues={highlightedResidues}
                                useOriginalSequence={true}
                                originalSequence={metadata.original_sequence}
                                highlightColor="red"
                                maxRows={6}
                              />
                            </div>
                          ) : (
                            <div className="mb-6">
                              <h4 className="text-sm font-medium text-gray-600 mb-3">Sequence View</h4>
                              <div className="flex items-center justify-center h-16 bg-gray-100 rounded-lg">
                                <p className="text-sm text-gray-500">Original sequence not available</p>
                              </div>
                            </div>
                          )}

                          {/* Original 3D Structure */}
                          <div className="flex-1 flex flex-col">
                            <h4 className="text-sm font-medium text-gray-600 mb-3">Structure Preview</h4>
                            <div className="flex-1">
                              {originalPdbContent ? (
                                <Molecule3DViewer
                                  key={`original-${job?.job_id}-${moleculeViewerId || 'default'}`}
                                  pdbContent={originalPdbContent}
                                  height="h-96"
                                  backgroundColor="white"
                                  showControls={true}
                                  highlightedResidues={highlightedResidues}
                                  highlightColor="red"
                                />
                              ) : moleculeViewerId ? (
                                <div className="flex items-center justify-center h-full bg-gray-100 rounded-lg">
                                  <div className="text-center">
                                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-600 mx-auto mb-3"></div>
                                    <p className="text-sm text-gray-600">Loading original structure...</p>
                                  </div>
                                </div>
                              ) : (
                                <div className="flex items-center justify-center h-full bg-gray-100 rounded-lg">
                                  <div className="text-center">
                                    <FileText className="h-10 w-10 text-gray-400 mx-auto mb-4" />
                                    <p className="text-sm text-gray-500">Original structure not available</p>
                                    <p className="text-xs text-gray-400 mt-2">View the original input structure by clicking</p>
                                    <p className="text-xs text-gray-400">"View Input Configuration" above</p>
                                  </div>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>

                        {/* Separator Line */}
                        <div className="w-px bg-gray-200 mx-4"></div>

                        {/* Generated Side - Right */}
                        <div className="flex-1 pl-4 flex flex-col">
                          <div className="flex items-center justify-between mb-6">
                            <h3 className="text-lg font-medium text-gray-700">
                              Generated Structure
                              {outputs.length > 0 && (
                                <span className="ml-2 text-sm text-gray-500">(gen {selectedGen + 1}{outputs.length ? ` / ${outputs.length}` : ''})</span>
                              )}
                            </h3>
                            {/* Legend for orange highlights */}
                            <div className="flex items-center gap-2 text-sm text-gray-600">
                              <span className="inline-block w-4 h-0.5 bg-orange-400"></span>
                              <span>= masked positions</span>
                            </div>
                          </div>

                          {/* Generated Sequence */}
                          {sequences.length > 0 && (
                            <div className="mb-6">
                              <h4 className="text-sm font-medium text-gray-600 mb-3">Sequence View</h4>
                              <EnhancedSequenceDisplay
                                sequences={sequences}
                                highlightedResidues={highlightedResidues}
                                highlightColor="orange"
                                maxRows={6}
                              />
                            </div>
                          )}

                          {/* Generated 3D Structure */}
                          <div className="flex-1 flex flex-col">
                            <h4 className="text-sm font-medium text-gray-600 mb-3">Structure Preview</h4>
                            <div className="flex-1">
                              <Molecule3DViewer
                                key={`generated-${job?.job_id}-${storedPdb ? 'stored' : 'url'}`}
                                pdbContent={storedPdb}
                                pdbUrl={!storedPdb ? pdbUrl : undefined}
                                height="h-96"
                                backgroundColor="white"
                                showControls={true}
                                highlightedResidues={highlightedResidues}
                                highlightColor="orange"
                              />
                            </div>
                          </div>
                        </div>
                      </div>
                    ) : (
                      /* Overlay Mode */
                      <div className="flex flex-1 min-h-80 flex-col">
                        {/* Top Section: Sequences side by side */}
                        <div className="flex">
                          {/* Original Side - Left (Sequence Only) */}
                          <div className="flex-1 pr-4 flex flex-col">
                            <div className="flex items-center justify-between mb-6">
                              <h3 className="text-lg font-medium text-gray-700">Original Structure</h3>
                              {/* Legend for red highlights */}
                              <div className="flex items-center gap-2 text-sm text-gray-600">
                                <span className="inline-block w-4 h-0.5 bg-red-400"></span>
                                <span>= masked positions</span>
                              </div>
                            </div>

                            {/* Original Sequence */}
                            {originalSequences.length > 0 ? (
                              <div className="mb-6">
                                <h4 className="text-sm font-medium text-gray-600 mb-3">Sequence View</h4>
                                <EnhancedSequenceDisplay
                                  sequences={originalSequences}
                                  highlightedResidues={highlightedResidues}
                                  highlightColor="red"
                                  maxRows={6}
                                />
                              </div>
                            ) : metadata?.original_sequence && sequences.length > 0 ? (
                              <div className="mb-6">
                                <h4 className="text-sm font-medium text-gray-600 mb-3">Sequence View</h4>
                                <EnhancedSequenceDisplay
                                  sequences={sequences}
                                  highlightedResidues={highlightedResidues}
                                  useOriginalSequence={true}
                                  originalSequence={metadata.original_sequence}
                                  highlightColor="red"
                                  maxRows={6}
                                />
                              </div>
                            ) : (
                              <div className="mb-6">
                                <h4 className="text-sm font-medium text-gray-600 mb-3">Sequence View</h4>
                                <div className="flex items-center justify-center h-16 bg-gray-100 rounded-lg">
                                  <p className="text-sm text-gray-500">Original sequence not available</p>
                                </div>
                              </div>
                            )}
                          </div>

                          {/* Separator Line (Top Section Only) */}
                          <div className="w-px bg-gray-200 mx-4"></div>

                          {/* Generated Side - Right (Sequence Only) */}
                          <div className="flex-1 pl-4 flex flex-col">
                            <div className="flex items-center justify-between mb-6">
                              <h3 className="text-lg font-medium text-gray-700">Generated Structure</h3>
                              {/* Legend for orange highlights */}
                              <div className="flex items-center gap-2 text-sm text-gray-600">
                                <span className="inline-block w-4 h-0.5 bg-orange-400"></span>
                                <span>= masked positions</span>
                              </div>
                            </div>

                            {/* Generated Sequence */}
                            {sequences.length > 0 && (
                              <div className="mb-6">
                                <h4 className="text-sm font-medium text-gray-600 mb-3">Sequence View</h4>
                                <EnhancedSequenceDisplay
                                  sequences={sequences}
                                  highlightedResidues={highlightedResidues}
                                  highlightColor="orange"
                                  maxRows={6}
                                />
                              </div>
                            )}
                          </div>
                        </div>

                        {/* Bottom Section: Centered Combined Structure Preview */}
                        <div className="flex-1 flex flex-col mt-6">
                          <div className="flex items-center justify-center mb-3">
                            <div className="flex items-center gap-2">
                              <h4 className="text-sm font-medium text-gray-600">Structure Preview</h4>
                              <div className="flex items-center gap-4 text-sm text-gray-600 ml-4">
                                {originalPdbContent && (
                                  <div className="flex items-center gap-2">
                                    <span className="inline-block w-4 h-3 bg-gray-500 rounded"></span>
                                    <span>original</span>
                                  </div>
                                )}
                                <div className="flex items-center gap-2">
                                  <span className="inline-block w-4 h-3 bg-blue-500 rounded"></span>
                                  <span>generated</span>
                                </div>
                              </div>
                            </div>
                          </div>
                          <div className="flex-1">
                            {(pdbUrl || storedPdb) ? (
                              <Molecule3DViewer
                                key={`overlay-${job?.job_id}-${originalPdbContent ? 'both' : 'single'}`}
                                multiplePdbContents={overlayPdbContents}
                                structureColors={overlayStructureColors}
                                structureLabels={overlayStructureLabels}
                                height="h-96"
                                backgroundColor="white"
                                showControls={true}
                                highlightedResidues={highlightedResidues}
                                highlightColor="red"
                              />
                            ) : (
                              <div className="flex items-center justify-center h-full bg-gray-100 rounded-lg">
                                <div className="text-center">
                                  <FileText className="h-10 w-10 text-gray-400 mx-auto mb-4" />
                                  <p className="text-sm text-gray-500">Generated structure not available</p>
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              ) : isProcessing ? (
                <div className="flex items-center justify-center h-64 bg-gray-100 rounded-lg">
                  <div className="text-center">
                    <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-purple-600 mx-auto mb-4"></div>
                    <p className="text-gray-600">Designing protein...</p>
                    {job.stage_details && (
                      <p className="text-sm text-gray-500 mt-2">{job.stage_details}</p>
                    )}
                  </div>
                </div>
              ) : isFailed ? (
                <div className="flex items-center justify-center h-64 bg-red-50 rounded-lg">
                  <div className="text-center">
                    <AlertTriangle className="h-10 w-10 text-red-500 mx-auto mb-4" />
                    <p className="text-red-600">Processing failed</p>
                    {job.stage_details && (
                      <p className="text-sm text-red-500 mt-2">{job.stage_details}</p>
                    )}
                  </div>
                </div>
              ) : isComplete ? (
                // Job is complete but no PDB data available yet
                <div className="flex items-center justify-center h-64 bg-gray-100 rounded-lg">
                  <div className="text-center">
                    {isConverting || savingNotes ? (
                      <>
                        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-gray-600 mx-auto mb-4"></div>
                        <p className="text-gray-600">
                          {savingNotes ? 'Saving notes...' : 'Converting structure data...'}
                        </p>
                      </>
                    ) : (
                      <>
                        <FileText className="h-10 w-10 text-gray-400 mx-auto mb-4" />
                        <p className="text-gray-600">Structure data not available</p>
                        <p className="text-sm text-gray-500 mt-2">The job completed but structure data is missing</p>
                      </>
                    )}
                  </div>
                </div>
              ) : (
                <div className="flex items-center justify-center h-64 bg-gray-100 rounded-lg">
                  <div className="text-center">
                    <Clock className="h-10 w-10 text-gray-400 mx-auto mb-4" />
                    <p className="text-gray-600">Waiting to start...</p>
                  </div>
                </div>
              )}
            </div>

          </div>

          {/* Sidebar - Right Side */}
          <div className="space-y-6">
            {/* Input Configuration Card */}
            <div className="bg-gray-50 rounded-lg shadow-sm p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <Search className="h-5 w-5" />
                Input Configuration
              </h3>

              <div className="space-y-2">
                <button
                  onClick={() => {
                    if (!moleculeViewerId) {
                      toast.error('Input configuration not available for this job');
                      return;
                    }
                    
                    openTab({
                      type: 'molecule',
                      title: moleculeViewerName || 'Input Configuration',
                      icon: Search,
                      closable: true,
                      projectId: job.project_id || '',
                      data: { 
                        moleculeId: moleculeViewerId
                      }
                    });
                  }}
                  className="w-full bg-gray-100 hover:bg-gray-200 text-gray-700 px-3 py-2 rounded-md transition-colors flex items-center gap-2 text-sm"
                >
                  <FileCode className="h-4 w-4" />
                  View Input Configuration
                </button>

                {/* Download Input JSON Button */}
                {job.json_input && (
                  <button
                    onClick={() => {
                      if (!job.json_input) {
                        toast.error('Input JSON data not available for this job');
                        return;
                      }
                      
                      handleDownload(
                        job.json_input,
                        `odyssey-${job.job_id}-input.json`,
                        true
                      );
                    }}
                    className="w-full bg-gray-100 hover:bg-gray-200 text-gray-700 px-3 py-2 rounded-md transition-colors flex items-center gap-2 text-sm"
                  >
                    <Download className="h-4 w-4" />
                    Download Input JSON
                  </button>
                )}
              </div>
            </div>

            {/* Timeline Card */}
            <div className="bg-gray-50 rounded-lg shadow-sm p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <Clock className="h-5 w-5" />
                Timeline
              </h3>

              <div className="space-y-3 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-600">Started</span>
                  <span className="text-gray-900">
                    {new Date(job.created_at).toLocaleString()}
                  </span>
                </div>

                {job.completed_at && (
                  <div className="flex justify-between">
                    <span className="text-gray-600">Completed</span>
                    <span className="text-gray-900">
                      {new Date(job.completed_at).toLocaleString()}
                    </span>
                  </div>
                )}
              </div>
            </div>

            {/* Metrics Card */}
            <div className="bg-gray-50 rounded-lg shadow-sm p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <Activity className="h-5 w-5" />
                Design Metrics
              </h3>

              <div className="space-y-3">
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-600">Total Residues</span>
                  <span className="text-sm font-medium text-gray-900">
                    {metadata?.num_residues || 'N/A'}
                  </span>
                </div>

                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-600">Designed Positions</span>
                  <span className="text-sm font-medium text-gray-900">
                    {metadata?.num_masked || 'N/A'}
                  </span>
                </div>
              </div>

            </div>

            {/* Downloads Card */}
            {isComplete && ((storedPdb || result?.pdb_output || result?.output_pdb_url) || (selectedOutput?.cif_output || job.cif_output)) && (
              <div className="bg-gray-50 rounded-lg shadow-sm p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                  <Download className="h-5 w-5" />
                  Downloads
                </h3>

                <div className="space-y-2">
                  {/* Download PDB Button */}
                  {(selectedOutput?.pdb_output || storedPdb || result?.pdb_output || result?.output_pdb_url) && (
                    <button
                      onClick={() => {
                        const genIdx = selectedOutput?.output_index;
                        const oneBased = genIdx !== undefined ? genIdx + 1 : undefined;
                        const filename = oneBased ? `odyssey-${job.job_id}-gen-${oneBased}.pdb` : `odyssey-${job.job_id}.pdb`;
                        if (selectedOutput?.pdb_output) {
                          handleDownload(selectedOutput.pdb_output, filename, true);
                        } else if (storedPdb) {
                          handleDownload(storedPdb, filename, true);
                        } else if (hasRawContent && result.pdb_output) {
                          handleDownload(result.pdb_output, filename, true);
                        } else if (result.output_pdb_url) {
                          handleDownload(result.output_pdb_url, filename, false);
                        }
                      }}
                      className="w-full bg-gray-100 hover:bg-gray-200 text-gray-700 px-3 py-2 rounded-md transition-colors flex items-center gap-2 text-sm"
                    >
                      <FileText className="h-4 w-4" />
                      {(() => {
                        const total = outputs.length > 0 ? outputs.length : 1;
                        const current = selectedOutput?.output_index !== undefined ? selectedOutput.output_index + 1 : 1;
                        return `PDB — gen ${current} / ${total}`;
                      })()}
                    </button>
                  )}

                  {/* Download CIF Button */}
                  {(selectedOutput?.cif_output || job.cif_output) && (
                    <button
                      onClick={() => {
                        const genIdx = selectedOutput?.output_index;
                        const oneBased = genIdx !== undefined ? genIdx + 1 : undefined;
                        const filename = oneBased ? `odyssey-${job.job_id}-gen-${oneBased}.cif` : `odyssey-${job.job_id}.cif`;
                        if (selectedOutput?.cif_output) {
                          handleDownload(selectedOutput.cif_output, filename, true);
                        } else if (job.cif_output) {
                          handleDownload(job.cif_output, filename, true);
                        }
                      }}
                      className="w-full bg-gray-100 hover:bg-gray-200 text-gray-700 px-3 py-2 rounded-md transition-colors flex items-center gap-2 text-sm"
                    >
                      <FileText className="h-4 w-4" />
                      {(() => {
                        const total = outputs.length > 0 ? outputs.length : 1;
                        const current = selectedOutput?.output_index !== undefined ? selectedOutput.output_index + 1 : 1;
                        return `CIF — gen ${current} / ${total}`;
                      })()}
                    </button>
                  )}
                </div>
              </div>
            )}

            {/* Run Next Generation Button */}
            {isComplete && (selectedOutput?.pdb_output || job.pdb_output || job.pdb_content) && (
              <div className="bg-gray-50 rounded-lg shadow-sm p-6">
                <button
                  onClick={handleRunNextGeneration}
                  className="w-full bg-purple-600 hover:bg-purple-700 text-white px-4 py-3 rounded-md transition-colors flex items-center justify-center gap-2 font-medium"
                >
                  <Rocket className="h-5 w-5" />
                  Run Next Generation
                  <ArrowRight className="h-4 w-4" />
                </button>
                <p className="text-xs text-gray-500 text-center mt-2">
                  {(() => {
                    const total = outputs.length > 0 ? outputs.length : 1;
                    const current = selectedOutput?.output_index !== undefined ? selectedOutput.output_index + 1 : 1;
                    return `Selected input for next generation: ${current}/${total}`;
                  })()}
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
    
    {/* Name Modal for Next Generation */}
    {showNameModal && (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-gray-50 rounded-lg shadow-xl p-6 w-full max-w-md">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">Name Your Next Generation</h2>
          <p className="text-sm text-gray-600 mb-4">
            Enter a name for this generation to help you identify it later.
          </p>
          <input
            type="text"
            value={nextGenName}
            onChange={(e) => setNextGenName(e.target.value.slice(0, 30))}
            maxLength={30}
            placeholder="e.g., Generation 3 - Enhanced Binding"
            className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:border-transparent ${
              nextGenName.length === 30
                ? 'border-red-500 focus:ring-red-500'
                : 'border-gray-300 focus:ring-purple-500'
            }`}
            autoFocus
            onKeyDown={(e) => {
              if (e.key === 'Enter' && nextGenName.trim()) {
                handleConfirmNextGeneration();
              }
            }}
          />
          {nextGenName.length === 30 && (
            <p className="text-xs text-red-500 mt-1">Maximum 30 characters</p>
          )}
          <div className="flex justify-end gap-3 mt-6">
            <button
              onClick={() => setShowNameModal(false)}
              className="px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md font-medium transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleConfirmNextGeneration}
              disabled={!nextGenName.trim()}
              className="px-4 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700 font-medium transition-colors disabled:bg-gray-300 disabled:cursor-not-allowed"
            >
              Continue
            </button>
          </div>
        </div>
      </div>
    )}
    </>
  );
}

// Wrap with error boundary for better realtime error handling
export function OdysseyJobDetails(props: OdysseyJobDetailsProps) {
  return (
    <RealtimeErrorBoundary>
      <OdysseyJobDetailsInner {...props} />
    </RealtimeErrorBoundary>
  );
}
